import { CalendarDays, Receipt } from "lucide-react";

import MeditationIcon from "../shared/ui/icons/MeditationIcon";

export type ToolNavItem = {
    name: string;
    icon: React.ReactNode;
    path: string;
};

/**
 * Nav entries that only exist once the therapist adopts the matching
 * marketplace tool, in the order they should appear.
 *
 * Both sit directly under Patients: booking a slot and billing for it are
 * things you do with a patient, so they belong beside them rather than down
 * by Settings.
 *
 * Instruments used *inside* a session — the constellation board, a genogram —
 * are not here. They open on a patient's own board and are reached from the
 * tool dock, which already knows which patient is in front of you; a sidebar
 * entry would land on a list with no context and ask again.
 */
export const TOOL_NAV_ITEMS: { slug: string; item: ToolNavItem }[] = [
    {
        slug: "scheduler",
        item: {
            name: "sidebar.scheduler",
            icon: <CalendarDays size={20} />,
            path: "scheduler",
        },
    },
    {
        slug: "payments",
        item: {
            name: "sidebar.payments",
            icon: <Receipt size={20} />,
            path: "payments",
        },
    },
    {
        // The library is written and organised outside sessions — it is the
        // one instrument here whose work happens before the patient arrives,
        // which is why it earns a sidebar entry the board and the genogram do
        // not.
        slug: "meditation",
        item: {
            name: "sidebar.meditations",
            icon: <MeditationIcon size={20} />,
            path: "meditations",
        },
    },
];

/**
 * The tool entries this therapist should see.
 *
 * Getting this wrong fails in both directions — hiding a tool they adopted,
 * or advertising one they did not — so it is kept as a plain function that
 * can be checked without mounting the sidebar.
 */
export const visibleToolNavItems = (
    adoptedSlugs: Iterable<string>
): ToolNavItem[] => {
    const adopted = new Set(adoptedSlugs);
    return TOOL_NAV_ITEMS.filter((entry) => adopted.has(entry.slug)).map(
        (entry) => entry.item
    );
};
